import { useCallback, useMemo, useState } from "react";
import { Badge, Button, Checkbox, Popover, Typography } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import type { ColumnType } from "antd/es/table";
import { columnHeaderText, type ExportColumn, type ExportColumns } from "../helpers/csv";

// One entry per table, so a user's choice on one report is not a choice on all of them.
const STORAGE_KEY = "glut_table_columns";

type HiddenMap = Record<string, string[]>;

function loadAll(): HiddenMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    // Storage is user-editable; keep only the shape this reads back.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>)
        .filter(([, v]) => Array.isArray(v) && v.every((k) => typeof k === "string"))
        .map(([k, v]) => [k, v as string[]]),
    );
  } catch {
    return {}; // corrupt or unreadable storage just means the default columns
  }
}

function saveOne(tableKey: string, hidden: string[]) {
  try {
    // Read-modify-write: another table's choices live under the same storage key.
    const all = loadAll();
    if (hidden.length) all[tableKey] = hidden;
    else delete all[tableKey];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // private mode or a full quota; the choice still holds for this page view
  }
}

/**
 * What identifies a column across reloads. `key`/`dataIndex` where a column has one, else
 * its header text — the header is what the checkbox says anyway, and it survives a column
 * being moved, which a positional index would not.
 */
function columnKeyOf<T>(col: ExportColumn<T>, index: number): string {
  if (col.key != null) return String(col.key);
  const dataIndex = (col as ColumnType<T>).dataIndex;
  if (dataIndex != null) return Array.isArray(dataIndex) ? dataIndex.join(".") : String(dataIndex);
  return columnHeaderText(col) || `col-${index}`;
}

export interface ColumnOption {
  key: string;
  label: string;
}

export interface ColumnPickerState<T> {
  /** The columns to hand the table and its export, in their declared order. */
  visible: ExportColumns<T>;
  /** Every hideable column, for the picker to draw. */
  options: ColumnOption[];
  hiddenKeys: string[];
  setHidden: (key: string, hidden: boolean) => void;
  reset: () => void;
  /** Nothing hidden — the picker's Reset has nothing to undo. */
  isDefault: boolean;
}

/**
 * Column visibility for a table, remembered per table in localStorage.
 *
 * `tableKey` names the table (a report key, a register name); it is what the choice is
 * filed under, so it has to be stable and unique across the app. Pass the returned
 * `visible` columns to both the table and its `TableDownload`: an export is the table as
 * rendered (architecture.md §12), so a column hidden on screen is out of the file too.
 */
export function useColumnPicker<T>(tableKey: string, columns: ExportColumns<T>): ColumnPickerState<T> {
  // The whole map is held, not one table's slice, so a report page that switches reports
  // under the same mounted hook picks up the new table's choice without a reload.
  const [hiddenMap, setHiddenMap] = useState<HiddenMap>(loadAll);

  const keyed = useMemo(() => {
    // Two columns deriving the same key (a repeated header, no `key` on either) would toggle
    // as one; the later one is suffixed so each checkbox owns exactly one column.
    const seen = new Map<string, number>();
    return columns.map((col, i) => {
      const base = columnKeyOf(col, i);
      const n = seen.get(base) ?? 0;
      seen.set(base, n + 1);
      return { col, key: n ? `${base}#${n}` : base, label: columnHeaderText(col) };
    });
  }, [columns]);

  // A column with no header has nothing to call it in the picker — in this app that is
  // always a row-action button, which stays put.
  const options = useMemo(() => keyed.filter((c) => c.label).map(({ key, label }) => ({ key, label })), [keyed]);

  const stored = useMemo(() => hiddenMap[tableKey] ?? [], [hiddenMap, tableKey]);
  // Keys left over from a column this report no longer has would otherwise show as hidden
  // columns nobody can bring back.
  const hiddenKeys = useMemo(() => {
    const hideable = new Set(options.map((o) => o.key));
    return stored.filter((k) => hideable.has(k));
  }, [stored, options]);

  const visible = useMemo(() => {
    if (!hiddenKeys.length) return columns;
    const hidden = new Set(hiddenKeys);
    return keyed.filter(({ key, label }) => !label || !hidden.has(key)).map(({ col }) => col);
  }, [columns, keyed, hiddenKeys]);

  const write = useCallback(
    (next: string[]) => {
      saveOne(tableKey, next);
      setHiddenMap((prev) => ({ ...prev, [tableKey]: next }));
    },
    [tableKey],
  );

  const setHidden = useCallback(
    (key: string, hidden: boolean) => write(hidden ? [...hiddenKeys, key] : hiddenKeys.filter((k) => k !== key)),
    [hiddenKeys, write],
  );

  const reset = useCallback(() => write([]), [write]);

  return { visible, options, hiddenKeys, setHidden, reset, isDefault: hiddenKeys.length === 0 };
}

/**
 * The button every table's column list is behind: check to show, uncheck to hide, one
 * Reset back to the columns the report ships with. Drive it from `useColumnPicker`.
 */
export function ColumnPicker<T>({ state, label = "Columns" }: { state: ColumnPickerState<T>; label?: string }) {
  const { options, hiddenKeys, setHidden, reset, isDefault } = state;
  const hidden = new Set(hiddenKeys);
  // Nothing to configure (a table of nothing but row actions) — no button either.
  const empty = options.length === 0;
  // A table with nothing in it is not a view anyone wants; the last column left is pinned.
  const lastVisible = options.length - hiddenKeys.length <= 1;

  const content = (
    <div style={{ minWidth: 200 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 8 }}>
        <Typography.Text type="secondary">Show columns</Typography.Text>
        <Button type="link" size="small" style={{ padding: 0 }} disabled={isDefault} onClick={reset}>
          Reset
        </Button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
        {options.map((o) => {
          const checked = !hidden.has(o.key);
          return (
            <Checkbox
              key={o.key}
              checked={checked}
              disabled={checked && lastVisible}
              onChange={(e) => setHidden(o.key, !e.target.checked)}
            >
              {o.label}
            </Checkbox>
          );
        })}
      </div>
    </div>
  );

  if (empty) return null;

  return (
    // The count says how many are hidden, so a missing column is never a mystery. The badge
    // is outside the Popover: what the Popover anchors to has to be the button itself.
    <Badge count={hiddenKeys.length} size="small" offset={[-4, 2]}>
      <Popover content={content} trigger="click" placement="bottomRight">
        <Button icon={<SettingOutlined />}>{label}</Button>
      </Popover>
    </Badge>
  );
}
